import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchArticles } from "store/articlesSlice";
import { Layout, Button } from "components/common/common";
import { articleApi } from "api/articleApi";
import { serverPath } from "config/config";
import * as XLSX from "xlsx-js-style";
import s from "./ListArticle.module.css";

const ACCENT_COLOR = "#7faac3";
const HEADER_TEXT_COLOR = "#ffffff";
const TITLE_LINE_LENGTH = 40;

const ListArticle = () => {
  const dispatch = useDispatch();
  const articles = useSelector((state) => state.articles.items);
  const status = useSelector((state) => state.articles.status);
  const error = useSelector((state) => state.articles.error);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    dispatch(fetchArticles());
  }, [dispatch]);

  const handleExportStats = async () => {
    if (!articles?.length || isExporting) return;

    setIsExporting(true);

    try {
      // Обновляем список статей перед экспортом, чтобы получить актуальные данные из метафайла
      let articlesToExport = articles;
      try {
        const freshArticlesResult = await dispatch(fetchArticles()).unwrap();
        if (freshArticlesResult && freshArticlesResult.length > 0) {
          articlesToExport = freshArticlesResult;
        }
      } catch (refreshError) {
        console.warn("Не удалось обновить список статей перед экспортом, используем данные из кэша", refreshError);
        // Продолжаем с существующими данными
      }

      // Используем свежие данные из метафайла (включая актуальные просмотры)
      const worksheetData = [
        ["Ссылка на статью", "Название статьи", "Описание для администрирования", "Количество просмотров"],
        ...articlesToExport.map((article) => {
          const linkFromMeta =
            article?.link ||
            article?.articleLink ||
            `${serverPath}/art/${article.hash}`;

          return [
            linkFromMeta,
            formatTitleForSheet(article?.title || ""),
            article?.shortDescription ?? "",
            article?.views ?? 0,
          ];
        }),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      applyWorksheetFormatting(worksheet, worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Статистика");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const exportDate = new Date().toISOString().split("T")[0];
      anchor.href = link;
      anchor.download = `stat_stat_${exportDate}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(link);
    } catch (exportError) {
      console.error("Ошибка при формировании статистики", exportError);
      alert("Не удалось сформировать файл статистики. Попробуйте снова.");
    } finally {
      setIsExporting(false);
    }
  };

  if (status === "loading") return <div>Loading...</div>;
  if (status === "failed") return <div>Error: {error}</div>;

  return (
    <div className={s.container}>
      <div className={s.header}>
        <h1>List of existing articles</h1>
        <Button
          label={isExporting ? "Формирование..." : "Выгрузить статистику"}
          onClick={handleExportStats}
          disabled={isExporting || !articles?.length}
          variant="secondary"
          className={s.exportButton}
        />
      </div>
      <Layout articles={articles} />
    </div>
  );
};

const formatTitleForSheet = (title = "", maxLineLength = TITLE_LINE_LENGTH) => {
  if (!title) return "";

  const words = title.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (candidate.length > maxLineLength) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        lines.push(word);
        currentLine = "";
      }
    } else {
      currentLine = candidate;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.join("\n");
};

const computeColumnWidths = (rows) => {
  if (!rows.length) {
    return [30, 30, 30, 23];
  }

  const dataRows = rows.slice(1);
  const maxLinkLength =
    dataRows.reduce((max, row) => Math.max(max, (row[0]?.length || 0) + 5), 30) || 30;
  const columnWidth = Math.min(100, Math.max(30, maxLinkLength));

  const maxCommentLength =
    dataRows.reduce((max, row) => Math.max(max, (row[2]?.length || 0) + 2), 30) || 30;
  const commentWidth = Math.min(80, Math.max(30, maxCommentLength));

  return [columnWidth, columnWidth, commentWidth, 23];
};

const hexToArgb = (hex) => {
  const normalized = hex.replace("#", "").padStart(6, "0").toUpperCase();
  return `FF${normalized}`;
};

const applyWorksheetFormatting = (worksheet, rows) => {
  if (!worksheet || !rows.length) return;

  const [linkWidth, titleWidth, commentWidth, viewsWidth] = computeColumnWidths(rows);
  worksheet["!cols"] = [
    { wch: linkWidth },
    { wch: titleWidth },
    { wch: commentWidth },
    { wch: viewsWidth },
  ];

  const totalRows = rows.length;
  const totalCols = rows[0]?.length || 0;
  worksheet["!rows"] = [{ hpt: 24 }, ...new Array(totalRows - 1)];

  for (let r = 0; r < totalRows; r++) {
    for (let c = 0; c < totalCols; c++) {
      const cellAddress = XLSX.utils.encode_cell({ r, c });
      const cell = worksheet[cellAddress];
      if (!cell) continue;

      const isHeader = r === 0;
      const nextAlignment = isHeader
        ? { horizontal: "center", vertical: "center" }
        : c === 3
        ? { horizontal: "center", vertical: "center" }
        : {
            horizontal: "left",
            vertical: c === 0 ? "center" : "top",
            wrapText: c === 1 || c === 2,
          };

      const nextStyle = {
        ...(cell.s || {}),
        alignment: nextAlignment,
        border: {
          top: { style: "thin", color: { rgb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { rgb: "FFCCCCCC" } },
          left: { style: "thin", color: { rgb: "FFCCCCCC" } },
          right: { style: "thin", color: { rgb: "FFCCCCCC" } },
        },
      };

      if (isHeader) {
        nextStyle.font = {
          ...(nextStyle.font || {}),
          bold: true,
          color: { rgb: hexToArgb(HEADER_TEXT_COLOR) },
        };
        nextStyle.fill = {
          ...(nextStyle.fill || {}),
          patternType: "solid",
          fgColor: { rgb: hexToArgb(ACCENT_COLOR) },
        };
      }

      cell.s = nextStyle;
    }
  }

  worksheet["!pageSetup"] = {
    ...(worksheet["!pageSetup"] || {}),
    centerHorizontal: true,
    centerVertical: true,
  };
};

export default ListArticle;
